import React, { useState, useCallback, useMemo, useEffect, useContext } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ThemeContext } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import { useNavigation } from '../hooks/useNavigation';
import { APP_NAME } from '../config/frontend';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import apiService from '../services/apiService';
import toast from 'react-hot-toast';
import { 
  BarChart3, 
  Package, 
  Users, 
  FileText, 
  ShoppingCart,
  TrendingDown,
  TrendingUp,
  CreditCard,
  DollarSign,
  Bell,
  Settings,
  Search,
  Calendar,
  ChevronDown,
  ChevronRight,
  Menu,
  Activity,
  PlusCircle,
  User,
  LogOut,
  RefreshCw,
  Filter,
  PanelLeftClose,
  PanelLeftOpen,
  Moon,
  Sun,
  ArrowUpRight, 
  ArrowDownRight,
  X,
  Plus,
  Receipt,
  ClipboardList,
  Eye,
  Edit,
  Trash2,
  Download,
  Send,
  AlertCircle,
  Info,
  Loader2,
  Tag,
  Box,
  Package2,
  Layers,
  Calculator,
  Phone,
  Mail,
  Printer,
  MapPin,
  Star,
  UserPlus,
  UserCheck
} from 'lucide-react';

const CustomerPayments = () => {
  const { logout, user } = useAuth();
  const { theme, toggleTheme } = useContext(ThemeContext);
  const { formatCurrency, currencySymbol } = useSettings();
  const { navigationItems } = useNavigation();
  const location = useLocation();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(location.pathname.startsWith('/customer') ? 3 : null);
  
  // Payment state management
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [paymentStats, setPaymentStats] = useState({
    total_payments: 0,
    total_amount: 0,
    completed_payments: 0,
    pending_payments: 0,
    failed_payments: 0,
    cancelled_payments: 0,
    payment_methods: []
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 15
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    method: 'all',
    search: '',
    sort_by: 'created_at',
    sort_order: 'desc'
  });

  // Add Payment Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [addPaymentLoading, setAddPaymentLoading] = useState(false);
  const [selectedCustomerDetails, setSelectedCustomerDetails] = useState(null);
  const [addPaymentForm, setAddPaymentForm] = useState({
    customer_id: '',
    amount: '',
    payment_method: 'cash',
    payment_date: new Date().toISOString().split('T')[0],
    payment_reference: '',
    notes: '',
    outstanding_balance: 0,
    // Payment method specific fields
    cash_breakdown: {},
    bank_name: '',
    account_name: '',
    account_number: '',
    mobile_network: '',
    sender_mobile_number: '',
    receiver_mobile_number: '',
    transaction_reference: '',
    card_number: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // Payment action modals state
  const [showViewModal, setShowViewModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [companySettings, setCompanySettings] = useState({
    company_name: 'NEXAPOS',
    company_address: '123 Business Street, Accra',
    company_phone: '+233 24 123 4567',
    tax_number: ''
  });


  const handleCardClick = useCallback((href) => {
    navigate(href);
  }, [navigate]);

  const handleFilterChange = useCallback((field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    // Reset to first page when filters change
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, []);

  // Cash breakdown calculation
  const calculateCashTotal = useCallback((breakdown) => {
    const denominations = [200, 100, 50, 20, 10, 5, 2, 1];
    return denominations.reduce((total, denom) => {
      const qty = breakdown[`${denom}_notes`] || 0;
      return total + (qty * denom);
    }, 0);
  }, []);

  const updateCashBreakdown = useCallback((denomination, quantity) => {
    setAddPaymentForm(prev => ({
      ...prev,
      cash_breakdown: {
        ...prev.cash_breakdown,
        [`${denomination}_notes`]: parseInt(quantity) || 0
      }
    }));
  }, []);

  // State for generated payment ID
  const [generatedPaymentId, setGeneratedPaymentId] = useState('');

  // Generate payment ID from API
  const generatePaymentId = useCallback(async () => {
    try {
      const response = await apiService.generateNextPaymentId();
      if (response.success) {
        setGeneratedPaymentId(response.payment_id);
        return response.payment_id;
      } else {
        console.error('Failed to generate payment ID:', response.message);
        // Fallback to local generation
        const today = new Date();
        const dateString = today.toISOString().slice(0, 10).replace(/-/g, '');
        const randomNumber = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        const fallbackId = `PAY-${dateString}${randomNumber}`;
        setGeneratedPaymentId(fallbackId);
        return fallbackId;
      }
    } catch (error) {
      console.error('Error generating payment ID:', error);
      // Fallback to local generation
      const today = new Date();
      const dateString = today.toISOString().slice(0, 10).replace(/-/g, '');
      const randomNumber = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      const fallbackId = `PAY-${dateString}${randomNumber}`;
      setGeneratedPaymentId(fallbackId);
      return fallbackId;
    }
  }, []);

  // Handle amount input with formatting
  const handleAmountChange = useCallback((value) => {
    // Remove commas and non-numeric characters except decimal point
    const cleanValue = value.replace(/[^\d.]/g, '');
    setAddPaymentForm(prev => ({
      ...prev,
      amount: cleanValue
    }));
  }, []);

  // Format amount for display
  const getFormattedAmount = useCallback((value) => {
    if (!value) return '';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;
    return numValue.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }, []);

  // Handle customer selection and update outstanding balance
  const handleCustomerChange = useCallback((customerId) => {
    const selectedCustomer = customers.find(customer => customer.id === parseInt(customerId));
    const balance = selectedCustomer ? parseFloat(selectedCustomer.balance || 0) : 0;
    
    setAddPaymentForm(prev => ({
      ...prev,
      customer_id: customerId,
      outstanding_balance: balance
    }));
    
    // Store selected customer details for display
    setSelectedCustomerDetails(selectedCustomer || null);
    
    // Clear search when customer is selected
    setCustomerSearchTerm('');
  }, [customers]);

  // Filter customers based on search term
  const filteredCustomers = useMemo(() => {
    if (!customerSearchTerm.trim()) {
      return customers;
    }
    
    const searchTerm = customerSearchTerm.toLowerCase().trim();
    return customers.filter(customer => 
      customer.name.toLowerCase().includes(searchTerm) ||
      customer.customerID?.toLowerCase().includes(searchTerm) ||
      customer.email?.toLowerCase().includes(searchTerm) ||
      customer.phone?.toLowerCase().includes(searchTerm)
    );
  }, [customers, customerSearchTerm]);

  // Group payments by customer and calculate totals
  const customerPaymentTotals = useMemo(() => {
    const groupedPayments = payments.reduce((acc, payment) => {
      const customerId = payment.customer_id;
      const customerName = payment.customer_name || `Customer ${customerId}`;
      const customerEmail = payment.customer_email || 'N/A';
      
      if (!acc[customerId]) {
        acc[customerId] = {
          customer_id: customerId,
          customer_name: customerName,
          customer_email: customerEmail,
          total_amount: 0,
          payment_count: 0,
          latest_payment_date: payment.payment_date,
          latest_payment_method: payment.payment_method,
          latest_payment_reference: payment.paymentID || payment.payment_reference
        };
      }
      
      acc[customerId].total_amount += parseFloat(payment.amount) || 0;
      acc[customerId].payment_count += 1;
      
      // Keep the latest payment date and details
      if (new Date(payment.payment_date) > new Date(acc[customerId].latest_payment_date)) {
        acc[customerId].latest_payment_date = payment.payment_date;
        acc[customerId].latest_payment_method = payment.payment_method;
        acc[customerId].latest_payment_reference = payment.paymentID || payment.payment_reference;
      }
      
      return acc;
    }, {});
    
    return Object.values(groupedPayments);
  }, [payments]);

  // Add Payment Modal handlers
  const handleAddPayment = useCallback(async () => {
    setShowAddModal(true);
    setIsEditMode(false);
    setSelectedPayment(null);
    
    // Generate payment ID
    const paymentId = await generatePaymentId();
    
    setAddPaymentForm({
      customer_id: '',
      amount: '',
      payment_method: 'cash',
      payment_date: new Date().toISOString().split('T')[0],
      payment_reference: paymentId,
      notes: '',
      outstanding_balance: 0,
      // Payment method specific fields
      cash_breakdown: {},
      bank_name: '',
      account_name: '',
      account_number: '',
      mobile_network: '',
      sender_mobile_number: '',
      receiver_mobile_number: '',
      transaction_reference: '',
      card_number: ''
    });
    setCustomerSearchTerm('');
    setFormErrors({});
  }, [generatePaymentId]);

  // Fetch payments
  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const apiParams = {
        page: pagination.currentPage,
        per_page: pagination.itemsPerPage,
        search: filters.search,
        method: filters.method,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order
      };
      const response = await apiService.getPayments(apiParams);
      
      setPayments(response.data || []);
      setPagination(prev => ({
        ...prev,
        totalPages: response.last_page || 1,
        totalItems: response.total || 0
      }));
      
      // Fetch stats separately
      await fetchPaymentStats();
    } catch (error) {
      console.error('❌ CustomerPayments - Error fetching payments:', error);
      console.error('❌ CustomerPayments - Error details:', error.message);
      toast.error('Failed to fetch payments: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.itemsPerPage, filters]);

  // Handle payment form submission
  const handleSubmitPayment = useCallback(async (e) => {
    e.preventDefault();
    
    try {
      // Prepare payment data
      const paymentData = {
        customer_id: addPaymentForm.customer_id,
        amount: addPaymentForm.amount,
        payment_method: addPaymentForm.payment_method,
        payment_date: new Date().toISOString().split('T')[0], // Today's date
        notes: addPaymentForm.notes,
        cash_breakdown: addPaymentForm.cash_breakdown,
        bank_name: addPaymentForm.bank_name,
        account_name: addPaymentForm.account_name,
        account_number: addPaymentForm.account_number,
        mobile_network: addPaymentForm.mobile_network,
        sender_mobile_number: addPaymentForm.sender_mobile_number,
        receiver_mobile_number: addPaymentForm.receiver_mobile_number,
        transaction_reference: addPaymentForm.transaction_reference,
        card_number: addPaymentForm.card_number,
      };


      // Submit payment
      const response = await apiService.addPayment(paymentData);
      
      if (response.success) {
        // Show success message
        toast.success('Payment added successfully!');
        
        // Set receipt data
        setReceiptData(response.data);
        
        // Close add modal
        setShowAddModal(false);
        
        // Show receipt modal
        setShowReceiptModal(true);
        
        // Reset form
        setAddPaymentForm({
          customer_id: '',
          amount: '',
          payment_method: 'cash',
          payment_date: new Date().toISOString().split('T')[0],
          payment_reference: '',
          notes: '',
          outstanding_balance: 0,
          cash_breakdown: {},
          bank_name: '',
          account_name: '',
          account_number: '',
          mobile_network: '',
          sender_mobile_number: '',
          receiver_mobile_number: '',
          transaction_reference: '',
          card_number: ''
        });
        
        // Refresh payments list
        fetchPayments();
      } else {
        toast.error(response.message || 'Failed to add payment');
      }
    } catch (error) {
      console.error('Error submitting payment:', error);
      toast.error('Failed to add payment. Please try again.');
    }
  }, [addPaymentForm, fetchPayments]);

  const handleViewPayment = useCallback((payment) => {
    setSelectedPayment(payment);
    setShowViewModal(true);
  }, []);

  const handleViewCustomerPayments = useCallback((customerId) => {
    // Filter payments for this specific customer
    const customerPayments = payments.filter(payment => payment.customer_id === customerId);
    setSelectedPayment(customerPayments[0]); // Show first payment as representative
    setShowViewModal(true);
  }, [payments]);

  const handleEditCustomerPayments = useCallback((customerId) => {
    // Filter payments for this specific customer and edit the latest one
    const customerPayments = payments.filter(payment => payment.customer_id === customerId);
    if (customerPayments.length > 0) {
      // Sort by date to get the latest payment
      const latestPayment = customerPayments.sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date))[0];
      handleEditPayment(latestPayment);
    }
  }, [payments]);

  const handleDeleteCustomerPayments = useCallback((customerId) => {
    // Filter payments for this specific customer
    const customerPayments = payments.filter(payment => payment.customer_id === customerId);
    if (customerPayments.length > 0) {
      // Show confirmation for deleting all payments
      const customerName = customerPayments[0].customer_name || `Customer ${customerId}`;
      if (window.confirm(`Are you sure you want to delete all ${customerPayments.length} payment(s) for ${customerName}? This action cannot be undone.`)) {
        // Delete all payments for this customer
        const deletePromises = customerPayments.map(payment => 
          apiService.deletePayment(payment.id)
        );
        
        Promise.all(deletePromises)
          .then(() => {
            fetchPayments(); // Refresh the payments list
            // Show success message
            const toast = document.createElement('div');
            toast.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
            toast.textContent = `Successfully deleted all payments for ${customerName}`;
            document.body.appendChild(toast);
            setTimeout(() => {
              document.body.removeChild(toast);
            }, 3000);
          })
          .catch(error => {
            console.error('❌ Error deleting customer payments:', error);
            // Show error message
            const toast = document.createElement('div');
            toast.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
            toast.textContent = 'Error deleting payments. Please try again.';
            document.body.appendChild(toast);
            setTimeout(() => {
              document.body.removeChild(toast);
            }, 3000);
          });
      }
    }
  }, [payments]);

  const handleEditPayment = useCallback((payment) => {
    setSelectedPayment(payment);
    setIsEditMode(true);
    setAddPaymentForm({
      customer_id: payment.customer_id || '',
      amount: payment.amount || '',
      payment_method: payment.payment_method || 'cash',
      payment_date: payment.payment_date || new Date().toISOString().split('T')[0],
      payment_reference: payment.payment_reference || '',
      notes: payment.notes || '',
      outstanding_balance: payment.balance_before_payment || 0,
      // Payment method specific fields
      cash_breakdown: payment.cash_breakdown || {},
      bank_name: payment.bank_name || '',
      account_name: payment.account_name || '',
      account_number: payment.account_number || '',
      mobile_network: payment.mobile_network || '',
      sender_mobile_number: payment.sender_mobile_number || '',
      receiver_mobile_number: payment.receiver_mobile_number || '',
      transaction_reference: payment.transaction_reference || '',
      card_number: payment.card_number || ''
    });
    setShowAddModal(true);
    setFormErrors({});
  }, []);

  const handleViewReceipt = useCallback((payment) => {
    setReceiptData(payment);
    setShowReceiptModal(true);
  }, []);

  // Fetch company settings
  const fetchCompanySettings = useCallback(async () => {
    try {
      const response = await apiService.getSettings();
      if (response.success) {
        setCompanySettings({
          company_name: response.data.company_name || 'NEXAPOS',
          company_address: response.data.company_address || '123 Business Street, Accra',
          company_phone: response.data.company_phone || '+233 24 123 4567',
          tax_number: response.data.tax_number || ''
        });
      }
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  }, []);

  const handlePrintReceipt = useCallback(() => {
    // Create a temporary div with the receipt content
    const printContent = document.createElement('div');
    printContent.innerHTML = `
      <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; background: white;">
        <!-- Header Section -->
        <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid #2563eb; padding-bottom: 20px;">
          <h1 style="font-size: 32px; font-weight: bold; color: #1f2937; margin: 0 0 8px 0; letter-spacing: 2px;">{APP_NAME}</h1>
          <p style="font-size: 16px; color: #6b7280; margin: 0 0 15px 0; font-weight: 500;">Customer Payment Receipt</p>
          <div style="display: flex; justify-content: space-between; align-items: center; font-size: 12px; color: #6b7280;">
            <span><strong>Payment ID:</strong> ${receiptData.paymentID}</span>
            <span><strong>Date:</strong> ${new Date(receiptData.created_at || receiptData.payment_date).toLocaleDateString('en-US')}</span>
            <span><strong>Time:</strong> ${new Date(receiptData.created_at || receiptData.payment_date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
            <span><strong>Processed by:</strong> ${user?.name || 'Agent'}</span>
          </div>
        </div>

        <!-- Customer and Company Section -->
        <div style="margin-bottom: 30px; display: flex; gap: 30px;">
          <!-- Customer Section (Left) -->
          <div style="flex: 1;">
            <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Customer Details</h3>
            <div style="background: #f8fafc; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0;">
              <div style="font-size: 18px; font-weight: bold; color: #1f2937; margin-bottom: 8px;">${receiptData.customer_name || receiptData.customer?.name || 'N/A'}</div>
              <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">📞 ${receiptData.customer_phone || receiptData.customer?.phone || 'N/A'}</div>
              <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">✉️ ${receiptData.customer_email || receiptData.customer?.email || 'N/A'}</div>
              <div style="font-size: 14px; color: #6b7280;">📍 ${receiptData.customer_address || receiptData.customer?.address || 'N/A'}</div>
            </div>
          </div>
          
          <!-- Company Section (Right) -->
          <div style="flex: 1;">
            <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Company Details</h3>
            <div style="background: #f8fafc; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0;">
              <div style="font-size: 18px; font-weight: bold; color: #1f2937; margin-bottom: 8px;">${companySettings.company_name}</div>
              <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">📍 ${companySettings.company_address}</div>
              <div style="font-size: 14px; color: #6b7280;">📞 ${companySettings.company_phone}</div>
              ${companySettings.tax_number ? `<div style="font-size: 14px; color: #6b7280;">Tax ID: ${companySettings.tax_number}</div>` : ''}
            </div>
          </div>
        </div>

        <!-- Payment Information Table -->
        <div style="margin-bottom: 30px;">
          <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 15px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Payment Information</h3>
          <table style="width: 100%; border-collapse: collapse; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;">
            <thead>
              <tr style="background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);">
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: left; font-weight: bold; color: #374151; font-size: 14px;">Payment Method</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">Amount</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #374151; font-size: 14px;">Status</th>
                <th style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; font-weight: bold; color: #374151; font-size: 14px;">Date</th>
              </tr>
            </thead>
            <tbody>
              <tr style="background: #f8fafc;">
                <td style="border: 1px solid #e2e8f0; padding: 12px; font-weight: 500; color: #1f2937; font-size: 14px; text-transform: capitalize;">${receiptData.payment_method?.replace('_', ' ')}</td>
                <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; font-weight: bold; color: #1f2937; font-size: 14px;">${currencySymbol} ${parseFloat(receiptData.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: center; color: #059669; font-size: 14px; font-weight: bold;">Completed</td>
                <td style="border: 1px solid #e2e8f0; padding: 12px; text-align: right; color: #374151; font-size: 14px;">${new Date(receiptData.created_at || receiptData.payment_date).toLocaleDateString('en-US')}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Balance Summary -->
        <div style="background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%); padding: 25px; border-radius: 12px; border: 1px solid #e2e8f0;">
          <h3 style="font-size: 18px; font-weight: bold; color: #1f2937; margin: 0 0 20px 0; border-left: 4px solid #2563eb; padding-left: 12px;">Balance Summary</h3>
          <div style="space-y: 12px;">
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #e2e8f0;">
              <span style="font-size: 16px; color: #6b7280; font-weight: 500;">Balance Before Payment:</span>
              <span style="font-size: 16px; font-weight: bold; color: #1f2937;">${currencySymbol} ${parseFloat(receiptData.balance_before_payment || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #e2e8f0;">
              <span style="font-size: 16px; color: #6b7280; font-weight: 500;">Amount Paid:</span>
              <span style="font-size: 16px; font-weight: bold; color: #059669;">${currencySymbol} ${parseFloat(receiptData.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 15px 0; border-top: 2px solid #2563eb; margin-top: 15px;">
              <span style="font-size: 20px; font-weight: bold; color: #1f2937;">New Balance:</span>
              <span style="font-size: 24px; font-weight: bold; color: #2563eb;">${currencySymbol} ${parseFloat(receiptData.balance_after_payment || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>

        ${receiptData.notes ? `
        <!-- Notes -->
        <div style="background: #f8fafc; padding: 20px; border-radius: 8px; border: 1px solid #e2e8f0; margin: 20px 0;">
          <h3 style="font-size: 16px; font-weight: bold; color: #1f2937; margin: 0 0 10px 0;">Notes</h3>
          <p style="font-size: 14px; color: #6b7280; margin: 0;">${receiptData.notes}</p>
        </div>
        ` : ''}

        <!-- Footer -->
        <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 2px solid #e2e8f0; color: #6b7280; font-size: 12px;">
          <p style="margin: 0 0 8px 0;">Thank you for your payment!</p>
          <p style="margin: 0 0 8px 0;">Keep this receipt for your records</p>
          <p style="margin: 0; font-weight: bold;">Powered By SherSoft Technologies - 0548104944</p>
        </div>
      </div>
    `;
    
    // Create hidden iframe for printing without opening new tab
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.left = '-9999px';
    iframe.style.top = '-9999px';
    iframe.style.width = '210mm';
    iframe.style.height = '297mm';
    iframe.style.border = 'none';
    
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(`
      <html>
        <head>
          <title>Customer Payment Receipt - ${receiptData.paymentID}</title>
          <style>
            @page {
              size: A4;
              margin: 20mm;
            }
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 0; 
              padding: 0;
              background: white;
              color: #1f2937;
            }
            .receipt { 
              width: 100%; 
              margin: 0 auto; 
              padding: 0;
            }
            @media print {
              body { margin: 0; padding: 0; }
              .receipt { border: none; }
            }
          </style>
        </head>
        <body>
          <div class="receipt">${printContent.innerHTML}</div>
        </body>
      </html>
    `);
    iframeDoc.close();
    
    // Wait for content to load, then print
    iframe.onload = () => {
      iframe.contentWindow.print();
      // Clean up iframe after printing
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    };
    
    // Show print notification
    toast.success('Customer payment receipt sent to printer!', {
      duration: 2000,
      icon: '🖨️',
      style: {
        background: '#10B981',
        color: '#fff',
        fontSize: '14px',
        padding: '12px',
        borderRadius: '8px',
      },
    });
  }, [receiptData, user, companySettings]);

  const handleCloseModals = useCallback(() => {
    setShowAddModal(false);
    setShowViewModal(false);
    setShowReceiptModal(false);
    setSelectedPayment(null);
    setReceiptData(null);
    setSelectedCustomerDetails(null);
    setAddPaymentForm({
      customer_id: '',
      amount: '',
      payment_method: 'cash',
      payment_date: new Date().toISOString().split('T')[0],
      payment_reference: '',
      notes: '',
      outstanding_balance: 0,
      // Payment method specific fields
      cash_breakdown: {},
      bank_name: '',
      account_name: '',
      account_number: '',
      mobile_network: '',
      sender_mobile_number: '',
      receiver_mobile_number: '',
      transaction_reference: '',
      card_number: ''
    });
    setCustomerSearchTerm('');
    setFormErrors({});
  }, []);


  // Fetch customers
  const fetchCustomers = useCallback(async () => {
    try {
      // Fetch all customers by setting a high per_page limit
      const response = await apiService.getCustomers('per_page=1000&status=active');
      
      if (response.success) {
        setCustomers(response.customers || []);
      } else {
        console.error('❌ CustomerPayments - Failed to fetch customers:', response.message);
        setCustomers([]);
      }
    } catch (error) {
      console.error('❌ CustomerPayments - Error fetching customers:', error);
      setCustomers([]);
    }
  }, []);

  // Fetch payment stats
  const fetchPaymentStats = useCallback(async () => {
    try {
      const response = await apiService.getPaymentStats();
      
      if (response.success && response.data) {
        const stats = {
          total_payments: response.data.total_payments || 0,
          total_amount: response.data.total_amount || 0,
          completed_payments: response.data.completed_payments || 0,
          pending_payments: response.data.pending_payments || 0,
          failed_payments: response.data.failed_payments || 0,
          cancelled_payments: response.data.cancelled_payments || 0,
          payment_methods: response.data.payment_methods || []
        };
        setPaymentStats(stats);
      } else {
        setPaymentStats({
          total_payments: 0,
          total_amount: 0,
          completed_payments: 0,
          pending_payments: 0,
          failed_payments: 0,
          cancelled_payments: 0,
          payment_methods: []
        });
      }
    } catch (error) {
      console.error('❌ CustomerPayments - Error fetching stats:', error);
      setPaymentStats({
        total_payments: 0,
        total_amount: 0,
        completed_payments: 0,
        pending_payments: 0,
        failed_payments: 0,
        cancelled_payments: 0,
        payment_methods: []
      });
    }
  }, []);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    fetchCompanySettings();
  }, [fetchCompanySettings]);

  // Update active dropdown when location changes
  useEffect(() => {
    if (location.pathname.startsWith('/customer')) {
      setActiveDropdown(3); // Customers submenu index
    } else if (location.pathname.startsWith('/product')) {
      setActiveDropdown(2); // Products submenu index
    } else if (location.pathname.startsWith('/pos') || location.pathname.startsWith('/retail') || location.pathname.startsWith('/bulk') || location.pathname.startsWith('/proforma') || location.pathname.startsWith('/credit')) {
      setActiveDropdown(1); // POS Operations submenu index
    } else if (location.pathname.startsWith('/report')) {
      setActiveDropdown(4); // Reports submenu index
    } else if (location.pathname.startsWith('/setting') || location.pathname.startsWith('/user')) {
      setActiveDropdown(5); // Settings submenu index
    } else {
      setActiveDropdown(null);
    }
  }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.user-menu-container')) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  // Add error boundary check
  if (!user) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Loading...</h2>
        <p className="text-gray-600">Please wait while we load your payments.</p>
      </div>
    </div>;
  }

  const getPaymentMethodBadge = (paymentMethod) => {
    const methodConfig = {
      cash: { color: 'bg-green-100 text-green-800', text: 'Cash' },
      bank_transfer: { color: 'bg-blue-100 text-blue-800', text: 'Bank Transfer' },
      mobile_money: { color: 'bg-orange-100 text-orange-800', text: 'Mobile Money' },
      credit_card: { color: 'bg-purple-100 text-purple-800', text: 'Credit Card' }
    };
    
    const config = methodConfig[paymentMethod] || methodConfig.cash;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getMethodBadge = (method) => {
    const methodConfig = {
      cash: { color: 'bg-green-100 text-green-800', text: 'Cash' },
      credit: { color: 'bg-blue-100 text-blue-800', text: 'Credit' },
      bank_transfer: { color: 'bg-purple-100 text-purple-800', text: 'Bank Transfer' },
      mobile_money: { color: 'bg-orange-100 text-orange-800', text: 'Mobile Money' },
      check: { color: 'bg-gray-100 text-gray-800', text: 'Check' }
    };
    
    const config = methodConfig[method] || methodConfig.cash;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  return (
    <Layout>
          {/* Main Content Area */}
      <main className={`flex-1 overflow-y-auto ${theme === 'dark' ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 via-white to-blue-50'} pt-20`}>
            <div className="max-w-7xl mx-auto p-6 space-y-6">
              {/* Page Header */}
              <div className="relative overflow-hidden bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 mb-6">
                {/* Background Pattern */}
                <div className="absolute inset-0 bg-gradient-to-br from-red-50 via-white to-blue-50 opacity-50"></div>
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full -translate-y-16 translate-x-16 opacity-60"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-red-100 to-orange-100 rounded-full translate-y-12 -translate-x-12 opacity-60"></div>
                
                <div className="relative z-10 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <CreditCard size={28} className="text-white" />
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-blue-600 bg-clip-text text-transparent mb-1">Customer Payments</h1>
                      <p className="text-gray-600 font-medium">Track and manage customer payments and transactions</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={fetchPayments}
                      disabled={loading}
                      className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-sm hover:shadow-md"
                    >
                      <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                      Refresh
                    </button>
                    <button 
                      onClick={() => navigate('/customers')}
                      className="px-4 py-2.5 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl transition-all duration-200 flex items-center gap-2 font-semibold shadow-lg hover:shadow-xl"
                    >
                      <Users size={18} />
                      Customers
                    </button>
                    <button 
                      onClick={handleAddPayment}
                      className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl transition-all duration-200 flex items-center gap-2 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      <Plus size={18} />
                      Add Payment
                    </button>
                  </div>
                </div>
              </div>

              {/* Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 hover:shadow-xl transition-all duration-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Payments</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        {loading ? <Loader2 className="animate-spin" size={20} /> : paymentStats.total_payments || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                      <CreditCard className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 hover:shadow-xl transition-all duration-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Amount</p>
                      <p className="text-xl font-bold text-gray-900 mt-1">
                        {loading ? <Loader2 className="animate-spin" size={20} /> : formatCurrency(paymentStats.total_amount || 0)}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                      <DollarSign className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>

                {/* Payment Method Statistics */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 hover:shadow-xl transition-all duration-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cash Payments</p>
                      <p className="text-xl font-bold text-gray-900 mt-1">
                        {loading ? <Loader2 className="animate-spin" size={20} /> : paymentStats.payment_methods?.find(m => m.payment_method === 'cash')?.count || 0}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {formatCurrency(paymentStats.payment_methods?.find(m => m.payment_method === 'cash')?.total || 0)}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold text-lg">{currencySymbol}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 hover:shadow-xl transition-all duration-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Mobile Money</p>
                      <p className="text-xl font-bold text-gray-900 mt-1">
                        {loading ? <Loader2 className="animate-spin" size={20} /> : paymentStats.payment_methods?.find(m => m.payment_method === 'mobile_money')?.count || 0}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {formatCurrency(paymentStats.payment_methods?.find(m => m.payment_method === 'mobile_money')?.total || 0)}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold text-lg">📱</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 hover:shadow-xl transition-all duration-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Bank Transfer</p>
                      <p className="text-xl font-bold text-gray-900 mt-1">
                        {loading ? <Loader2 className="animate-spin" size={20} /> : paymentStats.payment_methods?.find(m => m.payment_method === 'bank_transfer')?.count || 0}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {formatCurrency(paymentStats.payment_methods?.find(m => m.payment_method === 'bank_transfer')?.total || 0)}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold text-lg">🏦</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 hover:shadow-xl transition-all duration-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Credit Card</p>
                      <p className="text-xl font-bold text-gray-900 mt-1">
                        {loading ? <Loader2 className="animate-spin" size={20} /> : paymentStats.payment_methods?.find(m => m.payment_method === 'credit_card')?.count || 0}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {formatCurrency(paymentStats.payment_methods?.find(m => m.payment_method === 'credit_card')?.total || 0)}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                      <CreditCard className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>
                
              </div>


              {/* Filters and Search */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input 
                      type="text" 
                      placeholder="Search payments..." 
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 font-medium"
                    />
                  </div>
                  <select 
                    value={filters.method}
                    onChange={(e) => handleFilterChange('method', e.target.value)}
                    className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 font-medium"
                  >
                    <option value="all">All Methods</option>
                    <option value="cash">Cash</option>
                    <option value="credit">Credit</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="mobile_money">Mobile Money</option>
                    <option value="check">Check</option>
                  </select>
                  <select 
                    value={filters.sort_by}
                    onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                    className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 font-medium"
                  >
                    <option value="created_at">Sort by Date</option>
                    <option value="amount">Sort by Amount</option>
                    <option value="status">Sort by Status</option>
                    <option value="payment_method">Sort by Method</option>
                  </select>
                  <select 
                    value={pagination.itemsPerPage}
                    onChange={(e) => {
                      setPagination(prev => ({ 
                        ...prev, 
                        itemsPerPage: parseInt(e.target.value),
                        currentPage: 1 
                      }));
                    }}
                    className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 font-medium"
                  >
                    <option value={10}>10 per page</option>
                    <option value={15}>15 per page</option>
                    <option value={25}>25 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                  </select>
                  <button className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl transition-all duration-200 flex items-center gap-2 font-semibold shadow-lg hover:shadow-xl">
                    <Filter size={18} />
                    Apply Filters
                  </button>
                </div>
              </div>

              {/* Payments Table */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Reference
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Method
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {loading ? (
                        <tr>
                          <td colSpan="6" className="px-6 py-12 text-center">
                            <div className="flex items-center justify-center">
                              <Loader2 className="animate-spin h-8 w-8 text-blue-600" />
                              <span className="ml-2 text-gray-600">Loading payments...</span>
                            </div>
                          </td>
                        </tr>
                      ) : payments.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="px-6 py-12 text-center">
                            <div className="text-center">
                              <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
                              <h3 className="mt-2 text-sm font-medium text-gray-900">No payments found</h3>
                              <p className="mt-1 text-sm text-gray-500">
                                {filters.search || filters.method !== 'all' 
                                  ? 'Try adjusting your search or filters.' 
                                  : 'Get started by recording your first payment.'}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        payments.map((payment) => (
                          <tr key={payment.id} className="hover:bg-gray-50 transition-colors duration-200">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10">
                                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                    <span className="text-sm font-medium text-blue-600">
                                      {payment.customer_name ? payment.customer_name.charAt(0).toUpperCase() : 'C'}
                                    </span>
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {payment.customer_name || `Customer ${payment.customer_id}`}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {payment.customer_email || 'N/A'}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {payment.paymentID}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(payment.amount)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {getPaymentMethodBadge(payment.payment_method)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(payment.payment_date || payment.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleViewPayment(payment)}
                                  className="group/btn relative p-2 text-blue-600 bg-blue-100 hover:bg-blue-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                                  title="View"
                                >
                                  <Eye size={18} />
                                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                    View
                                  </span>
                                </button>
                                <button
                                  onClick={() => handleEditPayment(payment)}
                                  className="group/btn relative p-2 text-green-600 bg-green-100 hover:bg-green-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                                  title="Edit"
                                >
                                  <Edit size={18} />
                                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                    Edit
                                  </span>
                                </button>
                                <button
                                  onClick={() => handleViewReceipt(payment)}
                                  className="group/btn relative p-2 text-purple-600 bg-purple-100 hover:bg-purple-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110"
                                  title="View Receipt"
                                >
                                  <Printer size={18} />
                                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                    Receipt
                                  </span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-lg shadow-sm">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.max(1, prev.currentPage - 1) }))}
                      disabled={pagination.currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.min(prev.totalPages, prev.currentPage + 1) }))}
                      disabled={pagination.currentPage === pagination.totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing page <span className="font-medium">{pagination.currentPage}</span> of{' '}
                        <span className="font-medium">{pagination.totalPages}</span>
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.max(1, prev.currentPage - 1) }))}
                          disabled={pagination.currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                          <button
                            key={page}
                            onClick={() => setPagination(prev => ({ ...prev, currentPage: page }))}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              page === pagination.currentPage
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        ))}
                        <button
                          onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.min(prev.totalPages, prev.currentPage + 1) }))}
                          disabled={pagination.currentPage === pagination.totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </main>
      {/* Add Payment Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden border border-gray-200">
            {/* Header */}
            <div className="relative overflow-hidden bg-gradient-to-r from-blue-500 via-purple-600 to-blue-600 px-6 py-4 text-white">
              {/* Background Pattern */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12"></div>
              
              <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg border border-white/30">
                    <Plus size={24} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Add New Payment</h2>
                    <p className="text-white/90 text-sm font-medium">
                      Record a new customer payment
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={handleCloseModals}
                    className="px-5 py-2 text-white bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl transition-all duration-200 border border-white/30 shadow-lg hover:shadow-xl font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="add-payment-form"
                    className="px-6 py-2 bg-gradient-to-r from-red-500 to-blue-600 text-white rounded-xl hover:from-red-600 hover:to-blue-700 transition-all duration-200 flex items-center gap-2 font-medium shadow-lg hover:shadow-xl"
                  >
                    <Plus size={16} />
                    Add Payment
                  </button>
                </div>
              </div>
            </div>

            <form id="add-payment-form" onSubmit={handleSubmitPayment} className="overflow-y-auto max-h-[calc(95vh-140px)]">
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* LEFT SIDE - Basic Payment Information */}
                  <div className="space-y-4">
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200 shadow-sm">
                      <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                        <CreditCard size={20} className="text-blue-600" />
                        Payment Information
                      </h3>
                      
                      {/* Payment ID */}
                      <div className="mb-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Payment ID
                        </label>
                        <input
                          type="text"
                          value={addPaymentForm.payment_reference || generatedPaymentId || ''}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-100 text-gray-600 font-medium focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                          readOnly
                        />
                      </div>

                      {/* Customer Search */}
                      <div className="mb-3">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Search Customer
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={customerSearchTerm}
                            onChange={(e) => setCustomerSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 font-medium"
                            placeholder="Search by name, number, email, or phone..."
                          />
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-5 w-5 text-gray-400" />
                          </div>
                        </div>
                      </div>

                      {/* Customer Selection */}
                      <div className="mb-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Customer *
                        </label>
                        <select
                        value={addPaymentForm.customer_id}
                        onChange={(e) => handleCustomerChange(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 font-medium"
                        required
                      >
                          <option value="">Select Customer</option>
                          {filteredCustomers.map((customer) => (
                            <option key={customer.id} value={customer.id}>
                              {customer.name}
                            </option>
                          ))}
                        </select>
                        {customerSearchTerm && filteredCustomers.length === 0 && (
                          <p className="text-sm text-gray-500 mt-1">No customers found matching your search.</p>
                        )}
                      </div>


                      {/* Payment Date */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Date *
                        </label>
                        <input
                          type="date"
                          value={addPaymentForm.payment_date}
                          onChange={(e) => setAddPaymentForm(prev => ({ ...prev, payment_date: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>

                      {/* Payment Method */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Payment Method *
                        </label>
                        <select
                          value={addPaymentForm.payment_method}
                          onChange={(e) => setAddPaymentForm(prev => ({ ...prev, payment_method: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        >
                          <option value="">Select Payment Method</option>
                          <option value="cash">Cash</option>
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="mobile_money">Mobile Money</option>
                          <option value="credit_card">Credit Card</option>
                        </select>
                      </div>

                      {/* Payment Amount */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Amount *
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">{currencySymbol}</span>
                          <input
                            type="text"
                            value={getFormattedAmount(addPaymentForm.amount)}
                            onChange={(e) => handleAmountChange(e.target.value)}
                            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="0.00"
                            required
                          />
                        </div>
                      </div> 

                      {/* Outstanding Balance */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Outstanding Balance
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">{currencySymbol}</span>
                          <input
                            type="text"
                            value={addPaymentForm.outstanding_balance ? parseFloat(addPaymentForm.outstanding_balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                            readOnly
                          />
                        </div>
                        {(() => {
                          const outstandingBalance = addPaymentForm.outstanding_balance || 0;
                          const paymentAmount = parseFloat(addPaymentForm.amount) || 0;
                          const remainingBalance = outstandingBalance - paymentAmount;
                          
                          if (outstandingBalance > 0 && addPaymentForm.amount) {
                            if (remainingBalance > 0) {
                              return (
                                <p className="text-xs text-orange-600 mt-1">
                                  Customer has outstanding balance ({formatCurrency(remainingBalance)} remaining)
                                </p>
                              );
                            } else if (remainingBalance === 0) {
                              return (
                                <p className="text-xs text-green-600 mt-1">
                                  Customer account will be cleared with this payment
                                </p>
                              );
                            } else {
                              return (
                                <p className="text-xs text-blue-600 mt-1">
                                  Payment exceeds outstanding balance ({formatCurrency(Math.abs(remainingBalance))} overpayment)
                                </p>
                              );
                            }
                          } else if (outstandingBalance === 0 && addPaymentForm.customer_id) {
                            return (
                              <p className="text-xs text-green-600 mt-1">
                                Customer account is clear
                              </p>
                            );
                          } else if (outstandingBalance > 0 && !addPaymentForm.amount) {
                            return (
                              <p className="text-xs text-orange-600 mt-1">
                                Customer has outstanding balance ({formatCurrency(outstandingBalance)})
                              </p>
                            );
                          }
                          return null;
                        })()}
                      </div>

                      {/* Notes */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Notes
                        </label>
                        <textarea
                          value={addPaymentForm.notes}
                          onChange={(e) => setAddPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                          placeholder="Additional notes..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* RIGHT SIDE - Dynamic Payment Method Fields */}
                  <div className="space-y-4">
                    {/* Cash Breakdown */}
                    {addPaymentForm.payment_method === 'cash' && (
                      <div className="bg-gray-50 rounded-xl p-4">
                        <h3 className="text-base font-semibold text-gray-900 mb-4">Cash Breakdown</h3>
                        <div className="space-y-3">
                          {[200, 100, 50, 20, 10, 5, 2, 1].map((denomination) => (
                            <div key={denomination} className="grid grid-cols-3 gap-4 items-center">
                              <span className="text-sm font-medium text-gray-700 text-right">{currencySymbol} {denomination}</span>
                              <input
                                type="number"
                                min="0"
                                value={addPaymentForm.cash_breakdown[`${denomination}_notes`] || 0}
                                onChange={(e) => updateCashBreakdown(denomination, e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-center text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                              <span className="text-sm font-medium text-gray-900 text-right">
                                {formatCurrency(((addPaymentForm.cash_breakdown[`${denomination}_notes`] || 0) * denomination))}
                              </span>
                            </div>
                          ))}
                          <div className="border-t pt-3 mt-3">
                            <div className="grid grid-cols-3 gap-4 items-center">
                              <span className="text-sm font-semibold text-gray-900 text-right">TOTAL:</span>
                              <div></div>
                              <span className="text-lg font-bold bg-yellow-100 px-3 py-2 rounded text-right">
                                {formatCurrency(calculateCashTotal(addPaymentForm.cash_breakdown))}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Bank Transfer Fields */}
                    {addPaymentForm.payment_method === 'bank_transfer' && (
                      <div className="bg-gray-50 rounded-xl p-4">
                        <h3 className="text-base font-semibold text-gray-900 mb-4">Bank Details</h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Bank Name
                            </label>
                            <input
                              type="text"
                              value={addPaymentForm.bank_name}
                              onChange={(e) => setAddPaymentForm(prev => ({ ...prev, bank_name: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Bank name"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Account Name
                            </label>
                            <input
                              type="text"
                              value={addPaymentForm.account_name}
                              onChange={(e) => setAddPaymentForm(prev => ({ ...prev, account_name: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Account holder name"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Account Number
                            </label>
                            <input
                              type="text"
                              value={addPaymentForm.account_number}
                              onChange={(e) => setAddPaymentForm(prev => ({ ...prev, account_number: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Account number"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Mobile Money Fields */}
                    {addPaymentForm.payment_method === 'mobile_money' && (
                      <div className="bg-gray-50 rounded-xl p-4">
                        <h3 className="text-base font-semibold text-gray-900 mb-4">Mobile Money Details</h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Mobile Network (Phone Number)
                            </label>
                            <select
                              value={addPaymentForm.mobile_network}
                              onChange={(e) => setAddPaymentForm(prev => ({ ...prev, mobile_network: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Select Network</option>
                              <option value="mtn">MTN</option>
                              <option value="vodafone">Vodafone</option>
                              <option value="airteltigo">AirtelTigo</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Mobile Number (Sender's Number)
                            </label>
                            <input
                              type="text"
                              value={addPaymentForm.sender_mobile_number}
                              onChange={(e) => setAddPaymentForm(prev => ({ ...prev, sender_mobile_number: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="0241234567"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Mobile Number (Receiver's Number)
                            </label>
                            <input
                              type="text"
                              value={addPaymentForm.receiver_mobile_number}
                              onChange={(e) => setAddPaymentForm(prev => ({ ...prev, receiver_mobile_number: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="0247654321"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Transaction Reference
                            </label>
                            <input
                              type="text"
                              value={addPaymentForm.transaction_reference}
                              onChange={(e) => setAddPaymentForm(prev => ({ ...prev, transaction_reference: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Transaction reference"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Credit Card Fields */}
                    {addPaymentForm.payment_method === 'credit_card' && (
                      <div className="bg-gray-50 rounded-xl p-4">
                        <h3 className="text-base font-semibold text-gray-900 mb-4">Credit Card Details</h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Card Number (VISA: Debit/Credit Card)
                            </label>
                            <input
                              type="text"
                              value={addPaymentForm.card_number}
                              onChange={(e) => setAddPaymentForm(prev => ({ ...prev, card_number: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="**** **** **** 1234"
                              maxLength="4"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Transaction Reference
                            </label>
                            <input
                              type="text"
                              value={addPaymentForm.transaction_reference}
                              onChange={(e) => setAddPaymentForm(prev => ({ ...prev, transaction_reference: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Transaction reference"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

            </form>
          </div>
        </div>
      )}

      {/* View Payment Modal */}
      {showViewModal && selectedPayment && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden border border-gray-200">
            {/* Header */}
            <div className="relative overflow-hidden bg-gradient-to-r from-red-500 to-blue-600 px-6 py-4 text-white">
              {/* Background Pattern */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12"></div>
              
              <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg border border-white/30">
                    <Eye size={24} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Payment Details</h2>
                    <p className="text-white/90 text-sm font-medium">Payment ID: {selectedPayment.paymentID}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => handleViewReceipt(selectedPayment)}
                    className="px-4 py-2 text-white bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl transition-all duration-200 border border-white/30 shadow-lg hover:shadow-xl font-medium flex items-center gap-2"
                  >
                    <Printer size={16} />
                    Print Receipt
                  </button>
                  <button
                    onClick={handleCloseModals}
                    className="px-5 py-2 text-white bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl transition-all duration-200 border border-white/30 shadow-lg hover:shadow-xl font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Customer Information */}
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <User size={20} className="text-blue-600" />
                    Customer Information
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Customer Name</label>
                      <p className="text-gray-900 font-bold text-lg">{selectedPayment.customer_name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Email</label>
                      <p className="text-gray-900">{selectedPayment.customer_email || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Phone</label>
                      <p className="text-gray-900">{selectedPayment.customer_phone || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Address</label>
                      <p className="text-gray-900">{selectedPayment.customer_address || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Payment Information */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <CreditCard size={20} className="text-green-600" />
                    Payment Information
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Amount</label>
                      <p className="text-3xl font-bold text-green-600">
                        {formatCurrency(selectedPayment.amount)}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Payment Method</label>
                      <div className="mt-2">
                        {getPaymentMethodBadge(selectedPayment.payment_method)}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Date</label>
                      <p className="text-gray-900 font-medium">
                        {new Date(selectedPayment.payment_date || selectedPayment.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Processed By</label>
                      <p className="text-gray-900 font-medium">{selectedPayment.created_by || 'System'}</p>
                    </div>
                  </div>
                </div>

                {/* Payment Method Details */}
                {selectedPayment.payment_method === 'cash' && selectedPayment.cash_breakdown && (
                  <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-6 border border-yellow-200 md:col-span-2">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <DollarSign size={20} className="text-yellow-600" />
                      Cash Breakdown
                    </h3>
                    <div className="space-y-2">
                      {Object.entries(selectedPayment.cash_breakdown).map(([denomination, count]) => {
                        const value = denomination.replace('_notes', '');
                        const total = count * value;
                        return count > 0 ? (
                          <div key={denomination} className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">{currencySymbol} {value} × {count}</span>
                            <span className="font-medium">{currencySymbol} {total.toLocaleString()}</span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}

                {selectedPayment.payment_method === 'bank_transfer' && (
                  <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-6 border border-indigo-200 md:col-span-2">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Building2 size={20} className="text-indigo-600" />
                      Bank Transfer Details
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Bank Name</label>
                        <p className="text-gray-900">{selectedPayment.bank_name || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Account Name</label>
                        <p className="text-gray-900">{selectedPayment.account_name || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Account Number</label>
                        <p className="text-gray-900">{selectedPayment.account_number || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                )}

                {selectedPayment.payment_method === 'mobile_money' && (
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200 md:col-span-2">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Phone size={20} className="text-purple-600" />
                      Mobile Money Details
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Network</label>
                        <p className="text-gray-900 capitalize">{selectedPayment.mobile_network || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Sender Number</label>
                        <p className="text-gray-900">{selectedPayment.sender_mobile_number || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Receiver Number</label>
                        <p className="text-gray-900">{selectedPayment.receiver_mobile_number || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Transaction Reference</label>
                        <p className="text-gray-900">{selectedPayment.transaction_reference || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedPayment.notes && (
                  <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-6 border border-gray-200 md:col-span-2">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FileText size={20} className="text-gray-600" />
                      Notes
                    </h3>
                    <p className="text-gray-700 leading-relaxed">{selectedPayment.notes}</p>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {showReceiptModal && receiptData && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Background */}
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300"></div>
          
          {/* Modal Content */}
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-red-500 to-blue-600 px-8 py-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                      <Receipt className="w-5 h-5 text-white" />
                </div>
                <div>
                      <h2 className="text-xl font-bold text-white">Customer Payment Receipt</h2>
                      <p className="text-white/80 text-sm">ID: {receiptData.paymentID}</p>
                </div>
              </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handlePrintReceipt}
                      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      <span>Print</span>
                    </button>
                    <button
                      onClick={handleCloseModals}
                      className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Close
                    </button>
                  </div>
              </div>
            </div>

              {/* Modal Body */}
              <div className="p-8 max-h-[calc(95vh-140px)] overflow-y-auto">
                <div className="space-y-8">
                  {/* Header Section */}
                  <div className="space-y-6">
                    {/* Receipt Header */}
                    <div className="text-center">
                      <h1 className="text-3xl font-bold text-gray-800 mb-2">NEXAPOS</h1>
                      <p className="text-gray-600 font-medium">Customer Payment Receipt</p>
                      <div className="flex justify-between items-center text-sm text-gray-600 mt-4">
                        <span><strong>Payment ID:</strong> {receiptData.paymentID}</span>
                        <span><strong>Date:</strong> {new Date(receiptData.created_at || receiptData.payment_date).toLocaleDateString('en-US')}</span>
                        <span><strong>Time:</strong> {new Date(receiptData.created_at || receiptData.payment_date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                        <span><strong>Processed by:</strong> {user?.name || 'Agent'}</span>
                </div>
              </div>

                    {/* Customer and Company Section */}
                    <div className="flex gap-6">
                      {/* Customer Section (Left) */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                </div>
                          <h3 className="text-xl font-bold text-gray-800">Customer Details</h3>
                        </div>
                        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 space-y-3">
                          <div className="font-bold text-gray-900 text-lg">{receiptData.customer_name || receiptData.customer?.name || 'N/A'}</div>
                          <div className="text-gray-600 flex items-center space-x-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <span>{receiptData.customer_phone || receiptData.customer?.phone || 'N/A'}</span>
                          </div>
                          <div className="text-gray-600 flex items-center space-x-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <span>{receiptData.customer_email || receiptData.customer?.email || 'N/A'}</span>
                          </div>
                          <div className="text-gray-600 flex items-center space-x-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span>{receiptData.customer_address || receiptData.customer?.address || 'N/A'}</span>
                          </div>
                        </div>
                </div>

                      {/* Company Section (Right) */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                          </div>
                          <h3 className="text-xl font-bold text-gray-800">Company Details</h3>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6 space-y-3">
                          <div className="font-bold text-gray-900 text-lg">{companySettings.company_name}</div>
                          <div className="text-gray-600 flex items-center space-x-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span>{companySettings.company_address}</span>
                          </div>
                          <div className="text-gray-600 flex items-center space-x-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <span>{companySettings.company_phone}</span>
                          </div>
                          {companySettings.tax_number && (
                            <div className="text-gray-600 flex items-center space-x-2">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                              </svg>
                              <span>Tax ID: {companySettings.tax_number}</span>
                            </div>
                          )}
                        </div>
                      </div>
                  </div>
                </div>

                  {/* Payment Details */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center space-x-2">
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Payment Information</span>
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gradient-to-r from-gray-100 to-gray-50">
                            <th className="border border-gray-200 px-4 py-3 text-left text-sm font-semibold text-gray-700 rounded-l-lg">Payment Method</th>
                            <th className="border border-gray-200 px-4 py-3 text-center text-sm font-semibold text-gray-700">Amount</th>
                            <th className="border border-gray-200 px-4 py-3 text-center text-sm font-semibold text-gray-700">Status</th>
                            <th className="border border-gray-200 px-4 py-3 text-right text-sm font-semibold text-gray-700 rounded-r-lg">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="hover:bg-gray-50 transition-colors">
                            <td className="border border-gray-200 px-4 py-3 text-sm text-gray-800 font-medium">
                              <div className="flex items-center space-x-2">
                                {receiptData.payment_method === 'cash' && <DollarSign size={16} className="text-green-600" />}
                                {receiptData.payment_method === 'mobile_money' && <Phone size={16} className="text-purple-600" />}
                                {receiptData.payment_method === 'bank_transfer' && <CreditCard size={16} className="text-blue-600" />}
                                {receiptData.payment_method === 'credit_card' && <CreditCard size={16} className="text-indigo-600" />}
                                <span className="capitalize">{receiptData.payment_method?.replace('_', ' ')}</span>
                              </div>
                            </td>
                            <td className="border border-gray-200 px-4 py-3 text-center text-sm text-gray-800 font-semibold">
                              {formatCurrency(receiptData.amount)}
                            </td>
                            <td className="border border-gray-200 px-4 py-3 text-center text-sm">
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                Completed
                  </span>
                            </td>
                            <td className="border border-gray-200 px-4 py-3 text-right text-sm text-gray-800">
                              {new Date(receiptData.created_at || receiptData.payment_date).toLocaleDateString('en-US')}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                </div>

                  {/* Balance Summary */}
                  <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-6 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center space-x-2">
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span>Balance Summary</span>
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600 font-medium">Balance Before Payment:</span>
                        <span className="font-semibold text-gray-800 text-lg">{formatCurrency(receiptData.balance_before_payment || 0)}</span>
                  </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600 font-medium">Amount Paid:</span>
                        <span className="font-semibold text-green-600 text-lg">{formatCurrency(receiptData.amount)}</span>
                      </div>
                      <div className="border-t-2 border-gray-300 pt-4 mt-4">
                  <div className="flex justify-between items-center">
                          <span className="text-xl font-bold text-gray-800">New Balance:</span>
                          <span className="text-2xl font-bold text-blue-600">{formatCurrency(receiptData.balance_after_payment || 0)}</span>
                        </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {receiptData.notes && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Notes</h3>
                      <p className="text-gray-600 text-sm">{receiptData.notes}</p>
                </div>
              )}

                  {/* Footer */}
                  <div className="text-center text-gray-500 text-sm border-t border-gray-200 pt-6">
                    <p className="mb-2">Thank you for your payment!</p>
                    <p className="mb-2">Keep this receipt for your records</p>
                    <p className="font-semibold">Powered By SherSoft Technologies - 0548104944</p>
              </div>
            </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default CustomerPayments;
